import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Shield,
  Search,
  Plus,
  Pencil,
  Copy,
  Trash2,
  Users,
  ChevronRight,
} from 'lucide-react';
import { CompactKpiCard } from '@/components/kpi-card';
import { SettingsLayout } from '@/components/Pages/SettingsLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { MOCK_ROLES, generateRoleId } from '@/lib/roles-mock-data';
import { fetchPlatformRoles, createPlatformRole, deletePlatformRole } from '@/lib/data-service';
import { useDataMode } from '@/contexts/DataModeContext';
import type { Role } from '@/types/roles.types';

// ============================================================================
// Status Badge
// ============================================================================

function StatusBadge({ status }: { status: Role['status'] }) {
  return (
    <Badge
      variant={status === 'Active' ? 'default' : 'secondary'}
      className={
        status === 'Active'
          ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-100 border-slate-200'
      }
    >
      {status}
    </Badge>
  );
}

// ============================================================================
// Type Badge
// ============================================================================

function TypeBadge({ type }: { type: Role['type'] }) {
  return (
    <Badge
      variant="outline"
      className={
        type === 'Platform'
          ? 'border-primary/30 text-primary bg-primary/5'
          : 'border-muted-foreground/30 text-muted-foreground bg-muted/30'
      }
    >
      {type}
    </Badge>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export default function SettingsUserPermissionsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDemoMode } = useDataMode();

  // ── State ───────────────────────────────────────────────────────────────────
  const [roles, setRoles] = useState<Role[]>(isDemoMode ? MOCK_ROLES : []);
  const [isLoading, setIsLoading] = useState(!isDemoMode);

  // Load roles from API on mount (live mode only)
  useEffect(() => {
    const loadRoles = async () => {
      if (isDemoMode) return;
      try {
        const apiRoles = await fetchPlatformRoles();
        setRoles(apiRoles);
      } catch (err) {
        console.error('Failed to load platform roles:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadRoles();
  }, [isDemoMode]);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);

  // ── Derived values ──────────────────────────────────────────────────────────
  const filteredRoles = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return roles;
    return roles.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q),
    );
  }, [roles, searchQuery]);

  const totalUsers = useMemo(
    () => roles.reduce((sum, r) => sum + r.userCount, 0),
    [roles],
  );
  const platformRoleCount = useMemo(
    () => roles.filter((r) => r.type === 'Platform').length,
    [roles],
  );

  // ── Actions ─────────────────────────────────────────────────────────────────

  function handleEditRole(role: Role) {
    navigate(`/settings/user-permissions/${role.id}`);
  }

  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const DEFAULT_NEW_PERMISSIONS = {
    projectVisibility: 'managed-allocated' as const,
    projectActions: {
      create: false,
      edit: false,
      delete: false,
      changeStatus: false,
      archive: false,
      assignKeyPeople: false,
    },
    projectTabs: {
      overview: 'View' as const,
      financials: 'Hidden' as const,
      billing: 'Hidden' as const,
      salesBudget: 'Hidden' as const,
      costBudget: 'Hidden' as const,
      resourceBudget: 'Hidden' as const,
      costCentres: 'Hidden' as const,
      purchases: 'Hidden' as const,
      time: 'View' as const,
      expenses: 'View' as const,
      team: 'View' as const,
      resourcing: 'View' as const,
      milestones: 'View' as const,
      changeRequests: 'View' as const,
      audit: 'Hidden' as const,
      reports: 'Hidden' as const,
    },
    reports: {
      executiveDashboard: false,
      pmDashboard: false,
      projectTeamDashboard: false,
      plReport: false,
      wipReport: false,
      utilisationReport: false,
      budgetVarianceReport: false,
      auditLogReport: false,
      projectTransactionsReport: false,
      projectStatusReport: false,
      projectForecastReport: false,
      profitabilityReport: false,
    },
    settings: {
      generalSettings: false,
      configureAuditTrail: false,
      projectSettings: false,
      integrations: false,
      rolesAndPermissions: false,
      templates: false,
      notifications: false,
      viewAuditLogReport: false,
      viewProjectAuditTab: false,
    },
  };

  async function handleDuplicateRole(role: Role) {
    if (isCreating) return;
    setIsCreating(true);
    const duplicatedPermissions = role.permissions ?? DEFAULT_NEW_PERMISSIONS;
    try {
      if (!isDemoMode) {
        const created = await createPlatformRole({
          name: `${role.name} (Copy)`,
          description: role.description ?? '',
          isActive: true,
          permissions: duplicatedPermissions,
        });
        setRoles((prev) => [...prev, created]);
        toast.success(`"${role.name}" duplicated successfully.`);
        navigate(`/settings/user-permissions/${created.id}`);
      } else {
        const newRole: Role = {
          ...role,
          id: generateRoleId(),
          name: `${role.name} (Copy)`,
          type: 'Custom',
          status: 'Active',
          userCount: 0,
          createdFrom: role.id,
        };
        setRoles((prev) => [...prev, newRole]);
        toast.success(`"${role.name}" duplicated successfully.`);
        navigate(`/settings/user-permissions/${newRole.id}`);
      }
    } catch (err) {
      console.error('Failed to duplicate role:', err);
      toast.error('Failed to duplicate role.');
    } finally {
      setIsCreating(false);
    }
  }

  function handleDeleteRole(role: Role) {
    if (role.type === 'Platform') return;
    if (role.userCount > 0) {
      toast.error('Remove all users from this role before deleting.');
      return;
    }
    setDeleteTarget(role);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      if (!isDemoMode) {
        await deletePlatformRole(deleteTarget.id);
      }
      setRoles((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      toast.success(`"${deleteTarget.name}" has been deleted.`);
    } catch (err) {
      console.error('Failed to delete role:', err);
      toast.error('Failed to delete role.');
    } finally {
      setDeleteTarget(null);
      setIsDeleting(false);
    }
  }

  async function handleCreateRole() {
    if (isCreating) return;
    setIsCreating(true);
    try {
      if (!isDemoMode) {
        const created = await createPlatformRole({
          name: 'New Role',
          description: '',
          isActive: true,
          permissions: DEFAULT_NEW_PERMISSIONS,
        });
        setRoles((prev) => [...prev, created]);
        navigate(`/settings/user-permissions/${created.id}`);
      } else {
        const newRole: Role = {
          id: generateRoleId(),
          name: 'New Role',
          description: '',
          type: 'Custom',
          status: 'Active',
          userCount: 0,
          permissions: DEFAULT_NEW_PERMISSIONS,
        };
        setRoles((prev) => [...prev, newRole]);
        navigate(`/settings/user-permissions/${newRole.id}`);
      }
    } catch (err) {
      console.error('Failed to create role:', err);
      toast.error('Failed to create role.');
    } finally {
      setIsCreating(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <TooltipProvider>
      <SettingsLayout currentPath={location.pathname} onNavigate={navigate}>
        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-6 p-6">

              {/* ── Page header ──────────────────────────────────────────── */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="text-xl font-semibold">Roles &amp; Permissions</h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    Manage the roles in your organisation and control what each role can access.
                  </p>
                </div>
                <Button onClick={handleCreateRole} className="shrink-0">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Role
                </Button>
              </div>

              {/* ── Stat cards ───────────────────────────────────────────── */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <CompactKpiCard
                  title="Total Roles"
                  value={roles.length}
                  icon={Shield}
                  description="Active role definitions"
                  accentVariant="info"
                  className="shadow-none"
                />
                <CompactKpiCard
                  title="Total Users"
                  value={totalUsers}
                  icon={Users}
                  description="Users with assigned roles"
                  accentVariant="info"
                  className="shadow-none"
                />
                <CompactKpiCard
                  title="Platform Roles"
                  value={platformRoleCount}
                  icon={Shield}
                  description="Base system roles"
                  accentVariant="info"
                  className="shadow-none"
                />
              </div>

              {/* ── Search ───────────────────────────────────────────────── */}
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search roles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* ── Roles table ──────────────────────────────────────────── */}
              <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden" data-tour="settings-permissions-roles-list">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40 hover:bg-muted/40">
                      <TableHead className="font-semibold">Role Name</TableHead>
                      <TableHead className="font-semibold">Description</TableHead>
                      <TableHead className="font-semibold text-center">Users</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Type</TableHead>
                      <TableHead className="font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                          Loading roles…
                        </TableCell>
                      </TableRow>
                    ) : filteredRoles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                          {roles.length === 0 && !searchQuery
                            ? 'No roles have been created yet. Click "Create Role" to get started.'
                            : 'No roles found matching your search.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRoles.map((role) => (
                        <TableRow
                          key={role.id}
                          className="cursor-pointer hover:bg-accent/40 transition-colors"
                          onClick={() => handleEditRole(role)}
                        >
                          {/* Role Name */}
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{role.name}</span>
                              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100" />
                            </div>
                          </TableCell>

                          {/* Description */}
                          <TableCell className="text-muted-foreground text-sm max-w-xs">
                            <span className="line-clamp-2">{role.description || '—'}</span>
                          </TableCell>

                          {/* Users */}
                          <TableCell className="text-center">
                            <Badge variant="secondary" className="font-mono">
                              {role.userCount}
                            </Badge>
                          </TableCell>

                          {/* Status */}
                          <TableCell>
                            <StatusBadge status={role.status} />
                          </TableCell>

                          {/* Type */}
                          <TableCell>
                            <TypeBadge type={role.type} />
                          </TableCell>

                          {/* Actions */}
                          <TableCell className="text-right">
                            <div
                              className="flex items-center justify-end gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {/* Edit */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => handleEditRole(role)}
                                    aria-label={`Edit ${role.name}`}
                                    data-tour="settings-permissions-edit-btn"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Edit role</TooltipContent>
                              </Tooltip>

                              {/* Duplicate */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => handleDuplicateRole(role)}
                                    aria-label={`Duplicate ${role.name}`}
                                  >
                                    <Copy className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Duplicate role</TooltipContent>
                              </Tooltip>

                              {/* Delete */}
                              {role.type === 'Platform' ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 opacity-30 cursor-not-allowed"
                                        disabled
                                        aria-label="Cannot delete platform role"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>Platform roles cannot be deleted</TooltipContent>
                                </Tooltip>
                              ) : role.userCount > 0 ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 opacity-40 cursor-not-allowed"
                                        disabled
                                        aria-label="Remove all users before deleting"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>Remove all users before deleting</TooltipContent>
                                </Tooltip>
                              ) : (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                      onClick={() => handleDeleteRole(role)}
                                      aria-label={`Delete ${role.name}`}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Delete role</TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

            </div>
          </div>
        </div>

        {/* ── Delete confirmation dialog ──────────────────────────────────── */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete role?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

      </SettingsLayout>
    </TooltipProvider>
  );
}
